/**
 * ASC 606 Phase 11g — Milestone Confirmation Panel (11d MILESTONES_COMPLETED).
 *
 * Per-POB:
 *   - Append-only milestone events (11d) — cumulative_amount + reasonable-
 *     measure attestation (25-34) + optional invoice amount (55-18 R2I)
 *   - "Confirm Milestone" modal → POST /confirm-milestone (auto-fires
 *     recognition when fulfillment_trigger=EVENT_MILESTONE)
 *   - Inline RecognitionEventsPanel filtered to MILESTONES_COMPLETED
 *
 * Backend:
 *   GET /contracts/{id}/pobs/{pob_id}/milestones
 *   POST /contracts/{id}/pobs/{pob_id}/confirm-milestone
 */
import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount } from '../../constants';
import DataTable, { Column } from './DataTable';
import Modal from './Modal';
import RecognitionEventsPanel from './RecognitionEventsPanel';
import { FormRow, InlineError, inputStyle, btnPrimary, btnSecondary } from './CostLedgerPanel';

const API = `${API_V1}/revenue`;

interface MilestoneEvent {
  event_id: number;
  milestone_name: string;
  milestone_code: string | null;
  cumulative_amount: number;
  milestone_progress_percentage: number | null;
  milestone_satisfied_affirmed: boolean;
  attestation_note: string | null;
  confirmation_date: string | null;
  confirmed_by: number | null;
  source_reference: string | null;
  invoice_amount: number | null;
  invoice_reference: string | null;
  notes: string | null;
  created_at: string | null;
}

interface Props {
  contractId: number;
  pobId: number;
  onStateChanged?: () => void;
}

export default function MilestoneConfirmPanel({ contractId, pobId, onStateChanged }: Props) {
  const [events, setEvents] = useState<MilestoneEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API}/contracts/${contractId}/pobs/${pobId}/milestones?limit=500`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEvents(data.events || []);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, [contractId, pobId]);

  useEffect(() => {
    load();
  }, [load]);

  const cols: Column[] = [
    { key: 'confirmation_date', label: 'Date', width: 110 },
    { key: 'milestone_name', label: 'Milestone', render: (v: string) => <strong>{v}</strong> },
    {
      key: 'cumulative_amount',
      label: 'Cumulative',
      align: 'right',
      render: (v: number) => formatAmount(v),
    },
    {
      key: 'milestone_progress_percentage',
      label: '% POB',
      align: 'right',
      render: (v: number | null) => (v == null ? '-' : `${(v * 100).toFixed(1)}%`),
    },
    {
      key: 'milestone_satisfied_affirmed',
      label: '25-34 Affirmed',
      render: (v: boolean) => (
        <span
          style={{
            padding: '2px 8px',
            borderRadius: 4,
            fontSize: 10,
            fontWeight: 600,
            background: v ? 'rgba(34,197,94,0.15)' : 'rgba(234,179,8,0.15)',
            color: v ? 'var(--positive)' : 'var(--warning)',
          }}
        >
          {v ? 'YES' : 'NO'}
        </span>
      ),
    },
    {
      key: 'invoice_amount',
      label: 'Invoice (R2I)',
      align: 'right',
      render: (v: number | null) => (v == null ? '-' : formatAmount(v)),
    },
    { key: 'milestone_code', label: 'Code', render: (v: string | null) => v || '-' },
    { key: 'source_reference', label: 'Ref', render: (v: string | null) => v || '-' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={() => setConfirmOpen(true)} style={btnPrimary}>
          Confirm Milestone
        </button>
      </div>

      {error && (
        <div style={{ padding: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid var(--negative)', borderRadius: 4, fontSize: 12, color: 'var(--negative)' }}>
          {error}
        </div>
      )}
      {flash && (
        <div style={{ padding: 10, background: 'rgba(34,197,94,0.1)', border: '1px solid var(--positive)', borderRadius: 4, fontSize: 12, color: 'var(--positive)' }}>
          {flash}
        </div>
      )}

      <div>
        <h3 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-primary)' }}>
          Milestone Events (append-only)
        </h3>
        <DataTable
          columns={cols}
          rows={events}
          loading={loading}
          emptyMessage="No milestones confirmed yet."
          rowKey="event_id"
        />
      </div>

      <RecognitionEventsPanel
        contractId={contractId}
        pobId={pobId}
        filterMethod="MILESTONES_COMPLETED"
        title="Recognition Events (Milestones)"
      />

      <ConfirmMilestoneModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        contractId={contractId}
        pobId={pobId}
        onSaved={async (msg) => {
          setConfirmOpen(false);
          setFlash(msg);
          await load();
          if (onStateChanged) onStateChanged();
        }}
      />
    </div>
  );
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
  contractId: number;
  pobId: number;
  onSaved: (flash: string) => void;
}

function ConfirmMilestoneModal({ open, onClose, contractId, pobId, onSaved }: ModalProps) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [cumAmount, setCumAmount] = useState('');
  const [progressPct, setProgressPct] = useState('');
  const [satisfied, setSatisfied] = useState(false);
  const [attestation, setAttestation] = useState('');
  const [confirmDate, setConfirmDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [sourceRef, setSourceRef] = useState('');
  const [invoiceAmt, setInvoiceAmt] = useState('');
  const [invoiceRef, setInvoiceRef] = useState('');
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(null);
    if (!name.trim()) {
      setErr('Milestone name is required.');
      return;
    }
    const cum = parseFloat(cumAmount);
    if (!(cum > 0)) {
      setErr('Cumulative amount must be > 0.');
      return;
    }
    const pct = progressPct ? parseFloat(progressPct) : null;
    if (pct != null && (pct < 0 || pct > 1)) {
      setErr('Progress pct must be between 0 and 1 (fractional).');
      return;
    }
    setBusy(true);
    try {
      const body: any = {
        milestone_name: name.trim(),
        cumulative_amount: cum,
        milestone_satisfied_affirmed: satisfied,
        confirmation_date: confirmDate,
      };
      if (code.trim()) body.milestone_code = code.trim();
      if (pct != null) body.milestone_progress_percentage = pct;
      if (attestation.trim()) body.attestation_note = attestation.trim();
      if (sourceRef.trim()) body.source_reference = sourceRef.trim();
      if (invoiceAmt) body.invoice_amount = parseFloat(invoiceAmt);
      if (invoiceRef.trim()) body.invoice_reference = invoiceRef.trim();
      if (notes.trim()) body.notes = notes.trim();
      const res = await authFetch(
        `${API}/contracts/${contractId}/pobs/${pobId}/confirm-milestone`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        },
      );
      if (res.ok) {
        const data = await res.json();
        const recogStatus = data.recognition?.status || 'recorded';
        const delta = data.recognition?.period_delta;
        onSaved(
          data.idempotency_hit
            ? `Milestone ${code || name} already recorded (event ${data.milestone_event_id}).`
            : `Milestone recorded. Recognition: ${recogStatus}${delta ? ` — Δ ${formatAmount(delta)}` : ''}.`,
        );
        setName('');
        setCode('');
        setCumAmount('');
        setProgressPct('');
        setSatisfied(false);
        setAttestation('');
        setSourceRef('');
        setInvoiceAmt('');
        setInvoiceRef('');
        setNotes('');
      } else {
        const body2 = await res.json().catch(() => ({ detail: 'Unknown error' }));
        const detail = typeof body2.detail === 'string' ? body2.detail : body2.detail?.message || JSON.stringify(body2.detail);
        setErr(detail || `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setErr(e?.message || 'Network error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Confirm Milestone"
      description="ASC 606-10-25-33/34 + DART 8.5.6 — cumulative amount; reviewer-affirmed reasonable measure of progress."
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} style={btnSecondary} disabled={busy}>
            Cancel
          </button>
          <button type="button" onClick={submit} style={btnPrimary} disabled={busy}>
            {busy ? 'Saving...' : 'Confirm'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ gridColumn: '1 / 3' }}>
          <FormRow label="Milestone Name">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
          </FormRow>
        </div>
        <FormRow label="Milestone Code (optional, for idempotency)">
          <input type="text" value={code} onChange={(e) => setCode(e.target.value)} style={inputStyle} placeholder="e.g. MS-03-FAT" />
        </FormRow>
        <FormRow label="Confirmation Date">
          <input type="date" value={confirmDate} onChange={(e) => setConfirmDate(e.target.value)} style={inputStyle} />
        </FormRow>
        <FormRow label="Cumulative Amount">
          <input type="number" value={cumAmount} onChange={(e) => setCumAmount(e.target.value)} style={inputStyle} step="0.01" />
        </FormRow>
        <FormRow label="Progress % (0-1, optional)">
          <input type="number" value={progressPct} onChange={(e) => setProgressPct(e.target.value)} style={inputStyle} step="0.01" max="1" min="0" />
        </FormRow>
        <div style={{ gridColumn: '1 / 3' }}>
          <FormRow label="25-34 Reasonable Measure Attestation">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={satisfied} onChange={(e) => setSatisfied(e.target.checked)} />
              <span style={{ fontSize: 12 }}>
                I attest this milestone represents a reasonable measure of POB satisfaction (required for recognition to fire).
              </span>
            </label>
            <textarea
              value={attestation}
              onChange={(e) => setAttestation(e.target.value)}
              style={{ ...inputStyle, minHeight: 50, resize: 'vertical', marginTop: 6 }}
              placeholder="Attestation rationale (evidence this milestone = genuine satisfaction, not just a label)..."
            />
          </FormRow>
        </div>
        <FormRow label="Source Reference (SO line, task, clause)">
          <input type="text" value={sourceRef} onChange={(e) => setSourceRef(e.target.value)} style={inputStyle} />
        </FormRow>
        <FormRow label="Invoice Amount (55-18 R2I)">
          <input type="number" value={invoiceAmt} onChange={(e) => setInvoiceAmt(e.target.value)} style={inputStyle} step="0.01" />
        </FormRow>
        <FormRow label="Invoice Reference">
          <input type="text" value={invoiceRef} onChange={(e) => setInvoiceRef(e.target.value)} style={inputStyle} />
        </FormRow>
        <div style={{ gridColumn: '1 / 3' }}>
          <FormRow label="Notes">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...inputStyle, minHeight: 40, resize: 'vertical' }} />
          </FormRow>
        </div>
      </div>
      {err && <InlineError>{err}</InlineError>}
    </Modal>
  );
}
